import React, { useState, useRef, useEffect } from 'react';
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";
import { Upload, X, CheckCircle, AlertCircle, Plus, Pen, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const LifeInsuranceClaimForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenNumber, setTokenNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState('');

  // Signature refs and state
  const sigCanvas = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [deathCertificateFiles, setDeathCertificateFiles] = useState([{ id: Date.now(), file: null }]);

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    // Application Date & Payment Info
    applicationDate: getTodayDate(),
    depositAmount: '100',
    paymentFor: '',
    paymentMode: '', // offline/online

    // Applicant Information
    applicantName: '',
    applicantRelation: '', // father/son/husband
    applicantRelationName: '',
    applicantSignature: '',
    applicantAddress: '',
    applicantMobile: '',

    // Patient/Deceased Information
    patientName: '',
    doctorName: '',
    admissionDate: '',
    dischargeDeathDate: '',
    uhidNumber: '',
    outcomeType: '', // death or discharge

    // File Uploads
    deathCertificateFiles: [],
    claimFormBFiles: [],
    claimFormB1Files: [],
    policyBondFiles: [],
    nomineeIdFiles: []
  });

  // Handle multiple file uploads
  const handleMultipleFileChange = (e, fieldName) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const fileErrors = [];

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        fileErrors.push(`${file.name}: File too large (max 5MB)`);
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        fileErrors.push(`${file.name}: Invalid file type`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, [fieldName]: fileErrors.join(', ') }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ...validFiles]
    }));

    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [fieldName]: 'File size should be less than 5MB' }));
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, [fieldName]: 'Only JPG, PNG, and PDF files are allowed' }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const removeFile = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  // Signature handling functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignatureDataURL(null);
    setSignatureFile(null);
    setErrors(prev => ({ ...prev, applicantSignature: '' }));
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.toDataURL('image/png');
      setSignatureDataURL(dataURL);
      setErrors(prev => ({ ...prev, applicantSignature: '' }));
    } else {
      setErrors(prev => ({ ...prev, applicantSignature: 'Please draw your signature / कृपया अपना हस्ताक्षर करें' }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, applicantSignature: 'File size should be less than 2MB' }));
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, applicantSignature: 'Only JPG and PNG images allowed' }));
        return;
      }

      setSignatureFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataURL(reader.result);
        setErrors(prev => ({ ...prev, applicantSignature: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeathCertificateFileChange = (index, file) => {
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, deathCertificateFiles: 'File size should not exceed 5MB' }));
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, deathCertificateFiles: 'Only JPG, PNG and PDF files are allowed' }));
        return;
      }

      const newFiles = [...deathCertificateFiles];
      newFiles[index].file = file;
      setDeathCertificateFiles(newFiles);
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.deathCertificateFiles;
        return newErrors;
      });
    }
  };

  const addMoreDeathCertificateFiles = () => {
    setDeathCertificateFiles([...deathCertificateFiles, { id: Date.now(), file: null }]);
  };

  const removeDeathCertificateFile = (index) => {
    if (deathCertificateFiles.length > 1) {
      const newFiles = deathCertificateFiles.filter((_, i) => i !== index);
      setDeathCertificateFiles(newFiles);
    }
  };

  useEffect(() => {
    // Clear death certificate files when outcome changes to discharge
    if (formData.outcomeType === 'discharge') {
      setDeathCertificateFiles([{ id: Date.now(), file: null }]);
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.deathCertificateFiles;
        return newErrors;
      });
    }
  }, [formData.outcomeType]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.paymentMode) newErrors.paymentMode = 'Required / आवश्यक';
    if (!formData.applicantName.trim()) newErrors.applicantName = 'Required / आवश्यक';
    if (!formData.applicantRelation) newErrors.applicantRelation = 'Required / आवश्यक';
    if (!formData.applicantRelationName.trim()) newErrors.applicantRelationName = 'Required / आवश्यक';
    if (!formData.applicantAddress.trim()) newErrors.applicantAddress = 'Required / आवश्यक';
    if (!formData.applicantMobile.trim()) newErrors.applicantMobile = 'Required / आवश्यक';
    else if (!/^\d{10}$/.test(formData.applicantMobile)) {
      newErrors.applicantMobile = 'Invalid Mobile Number / अमान्य मोबाइल नंबर';
    }

    if (!formData.patientName.trim()) newErrors.patientName = 'Required / आवश्यक';
    if (!formData.doctorName.trim()) newErrors.doctorName = 'Required / आवश्यक';
    if (!formData.admissionDate) newErrors.admissionDate = 'Required / आवश्यक';
    if (!formData.dischargeDeathDate) newErrors.dischargeDeathDate = 'Required / आवश्यक';
    if (!formData.outcomeType) newErrors.outcomeType = 'Required / आवश्यक';

    // Signature validation
    if (!signatureDataURL) {
      newErrors.applicantSignature = 'Applicant signature is required / प्रार्थी का हस्ताक्षर आवश्यक है';
    }

    // File validation
    // File validations - at least 1 file each
    // if (formData.deathCertificateFiles.length === 0) {
    //   newErrors.deathCertificateFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    // }
    // Death Certificate - ONLY mandatory when outcome is 'death'
    if (formData.outcomeType === 'death') {
      if (deathCertificateFiles.length === 0 || deathCertificateFiles.every(f => !f.file)) {
        newErrors.deathCertificateFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
      }
    }

    if (formData.claimFormBFiles.length === 0) {
      newErrors.claimFormBFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }
    if (formData.claimFormB1Files.length === 0) {
      newErrors.claimFormB1Files = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }
    if (formData.policyBondFiles.length === 0) {
      newErrors.policyBondFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }
    if (formData.nomineeIdFiles.length === 0) {
      newErrors.nomineeIdFiles = 'At least one file required / कम से कम एक फ़ाइल आवश्यक';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      Object.keys(formData).forEach(key => {
        if (!key.includes('Files')) {
          if (formData[key] !== null && formData[key] !== '') {
            submitData.append(key, formData[key]);
          }
        }
      });

      // Append applicant signature
      if (signatureMode === 'draw' && signatureDataURL) {
        const response = await fetch(signatureDataURL);
        const blob = await response.blob();
        submitData.append('applicantSignature', blob, 'applicant-signature.png');
      } else if (signatureMode === 'upload' && signatureFile) {
        submitData.append('applicantSignature', signatureFile);
      }

      // Append multiple files
      // formData.deathCertificateFiles?.forEach(file => {
      //   submitData.append('deathCertificateFiles', file);
      // });
      // Append Death Certificate files - ONLY if outcome is 'death'
      if (formData.outcomeType === 'death') {
        deathCertificateFiles.forEach(fileObj => {
          if (fileObj.file) {
            submitData.append('deathCertificateFiles', fileObj.file);
          }
        });
      }
      formData.claimFormBFiles?.forEach(file => {
        submitData.append('claimFormBFiles', file);
      });
      formData.claimFormB1Files?.forEach(file => {
        submitData.append('claimFormB1Files', file);
      });
      formData.policyBondFiles?.forEach(file => {
        submitData.append('policyBondFiles', file);
      });
      formData.nomineeIdFiles?.forEach(file => {
        submitData.append('nomineeIdFiles', file);
      });

      const response = await fetch('https://wellnessplix.in/formProxy.php?endpoint=life-insurance-claim', {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit form');
      }

      if (result.success) {
        setTokenNumber(result.data.submissionNo);
        setPdfUrl(result.data.pdfUrl);
        setSuccess(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      applicationDate: '',
      depositAmount: '100',
      paymentMode: '',
      certificateType: '',
      applicantName: '',
      applicantRelation: '',
      applicantRelationName: '',
      applicantSignature: '',
      applicantAddress: '',
      applicantMobile: '',
      patientName: '',
      doctorName: '',
      admissionDate: '',
      dischargeDeathDate: '',
      outcomeType: '',
      deathCertificateFiles: [],
      claimFormBFiles: [],
      claimFormB1Files: [],
      policyBondFiles: [],
      nomineeIdFiles: []
    });
    clearSignature();
    setSuccess(false);
    setTokenNumber('');
    setErrors({});
  };

  if (success) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-20 h-20 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                सफलतापूर्वक जमा किया गया!<br />
                Successfully Submitted!
              </h2>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">
                  आपका टोकन नंबर / Your Token Number
                </p>
                <p className="text-4xl font-bold text-blue-600 mb-4">
                  {tokenNumber}
                </p>
                <p className="text-sm text-gray-500">
                  यह नंबर व्हाट्सएप पर भेज दिया गया है<br />
                  This number has been sent to WhatsApp
                </p>
              </div>

              {/* PDF Download Button */}
              {pdfUrl && (
                <div className="mb-6">
                  <a
                    href={`http://3.6.79.46:8000${pdfUrl}`}
                    download
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF / पीडीएफ डाउनलोड करें
                  </a>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={resetForm}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  नया आवेदन भरें / Fill New Application
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  होम पर जाएं / Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const FileUploadSection = ({ fieldName, label, labelEn, color = 'blue' }) => (
    <div className="mb-6">
      <label className="block text-gray-700 font-semibold mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <p className="text-sm text-gray-500 mb-2">
        {labelEn} | You can upload multiple files / आप कई फ़ाइलें अपलोड कर सकते हैं
      </p>

      {formData[fieldName].length > 0 && (
        <div className="space-y-2 mb-3">
          {formData[fieldName].map((file, index) => (
            <div key={index} className={`flex items-center justify-between bg-${color}-50 p-3 rounded-lg`}>
              <div className="flex items-center gap-2 flex-1">
                <span className={`text-xs bg-${color}-200 text-${color}-800 px-2 py-1 rounded`}>
                  {index + 1}
                </span>
                <span className="text-sm text-gray-700 truncate">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(fieldName, index)}
                className="ml-2 text-red-500 hover:text-red-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${errors[fieldName] ? 'border-red-500 bg-red-50' : `border-gray-300 hover:border-${color}-400`
        }`}>
        <label className="cursor-pointer">
          <div className="flex flex-col items-center">
            {formData[fieldName].length === 0 ? (
              <>
                <Upload className="w-12 h-12 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  फ़ाइल अपलोड करें / Click to upload
                </span>
              </>
            ) : (
              <>
                <Plus className={`w-10 h-10 text-${color}-600 mb-2`} />
                <span className={`text-sm text-${color}-600 font-semibold`}>
                  और फ़ाइल जोड़ें / Add More Files
                </span>
              </>
            )}
            <span className="text-xs text-gray-400 mt-2">
              JPG, PNG, PDF (Max 5MB each)
            </span>
          </div>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            multiple
            onChange={(e) => handleMultipleFileChange(e, fieldName)}
            className="hidden"
          />
        </label>
      </div>
      {errors[fieldName] && (
        <p className="text-red-500 text-sm mt-1">⚠️ {errors[fieldName]}</p>
      )}
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="text-center mb-4">
              <p className="text-lg font-semibold text-gray-700 mb-2">📝 आवेदन प्रारूप</p>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                जीवन बीमा दावा प्रपत्र
              </h1>
              <p className="text-xl md:text-2xl text-gray-700">
                Life Insurance Claim Form
              </p>
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm md:text-base text-gray-700">
                <span className="font-semibold">सेवा में / To:</span><br />
                आचार्य प्रभारी, ट्रॉमा सेंटर<br />
                Acharya Prabhari, Trauma Center<br />
                चिकित्सा विज्ञान संस्थान, काशी हिन्दू विश्वविद्यालय<br />
                Institute of Medical Sciences, Banaras Hindu University
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* Application Date & Payment Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                आवेदन जानकारी / Application Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Application Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    दिनांक / Date
                  </label>
                  <input
                    type="date"
                    name="applicationDate"
                    value={formData.applicationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Deposit Amount - Hard-coded and Disabled */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Deposit Rs. (जमा राशि)
                  </label>
                  <input
                    type="number"
                    name="depositAmount"
                    value={formData.depositAmount}
                    onChange={handleInputChange}
                    disabled={true}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fixed amount / निश्चित राशि: ₹100
                  </p>
                </div>

                {/* Payment Mode */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    भुगतान का माध्यम / Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="online"
                        checked={formData.paymentMode === 'online'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">ऑनलाइन / Online</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMode"
                        value="offline"
                        checked={formData.paymentMode === 'offline'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">ऑफलाइन / Offline</span>
                    </label>
                  </div>
                  {errors.paymentMode && (
                    <p className="text-red-500 text-sm mt-1">{errors.paymentMode}</p>
                  )}
                </div>

                {/* Certificate Type
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    प्रमाण पत्र का प्रकार / Certificate Type
                  </label>
                  <select
                    name="certificateType"
                    value={formData.certificateType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">चुनें / Select</option>
                    <option value="birth">जन्म प्रमाण पत्र / Birth Certificate</option>
                    <option value="death">मृत्यु प्रमाण पत्र / Death Certificate</option>
                    <option value="medical">चिकित्सा प्रमाण पत्र / Medical Certificate</option>
                    <option value="life-insurance">जीवन बीमा / Life Insurance</option>
                    <option value="photocopy">छाया प्रति / Photocopy</option>
                  </select>
                </div> */}
              </div>
            </div>

            {/* Application Content Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                आवेदन विवरण / Application Details
              </h2>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700 mb-4">
                  <span className="font-semibold">महोदय / Sir,</span>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  निवेदन है कि प्रार्थी/प्रार्थिनी के <span className="font-semibold">(relation)</span> का इलाज आपके चिकित्सालय में
                  डॉक्टर के अंतर्गत से भर्ती होकर प्रारम्भ हुआ और को उनका देहांत / डिस्चार्ज हो गया।
                </p>
                <p className="text-gray-700 leading-relaxed mt-2 text-sm">
                  <em>Request that the treatment of the applicant's (relation) was started in your hospital under
                    the care of Doctor from (admission date) and ended on (discharge/death date).</em>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Applicant Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    प्रार्थी/प्रार्थिनी का नाम / Applicant Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="applicantName"
                    value={formData.applicantName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantName && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantName}</p>
                  )}
                </div>

                {/* Applicant Relation */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    संबंध / Relation <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="applicantRelation"
                    value={formData.applicantRelation}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantRelation ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">चुनें / Select</option>
                    <option value="father">पिता / Father</option>
                    <option value="son">पुत्र / Son</option>
                    <option value="husband">पति / Husband</option>
                    <option value="wife">पत्नी / Wife</option>
                    <option value="daughter">पुत्री / Daughter</option>
                    <option value="mother">माता / Mother</option>
                    <option value="other">अन्य / Other</option>
                  </select>
                  {errors.applicantRelation && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantRelation}</p>
                  )}
                </div>

                {/* Relation Person Name */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    पिता/पुत्र/पति का नाम / Father/Son/Husband Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="applicantRelationName"
                    value={formData.applicantRelationName}
                    onChange={handleInputChange}
                    placeholder="नाम दर्ज करें / Enter name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantRelationName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantRelationName && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantRelationName}</p>
                  )}
                </div>

                {/* Applicant Address */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    पता / Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="applicantAddress"
                    value={formData.applicantAddress}
                    onChange={handleInputChange}
                    placeholder="पूरा पता दर्ज करें / Enter full address"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantAddress && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantAddress}</p>
                  )}
                </div>

                {/* Applicant Mobile */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    मोबाइल नंबर / Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="applicantMobile"
                    value={formData.applicantMobile}
                    onChange={handleInputChange}
                    placeholder="10 अंकों का मोबाइल नंबर / 10 digit mobile"
                    maxLength="10"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.applicantMobile ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.applicantMobile && (
                    <p className="text-red-500 text-sm mt-1">{errors.applicantMobile}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Patient/Treatment Information Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                रोगी/उपचार की जानकारी / Patient/Treatment Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Patient Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    रोगी का नाम / Patient Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    placeholder="पूरा नाम दर्ज करें / Enter full name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.patientName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.patientName && (
                    <p className="text-red-500 text-sm mt-1">{errors.patientName}</p>
                  )}
                </div>

                {/* Doctor Name */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    डॉक्टर का नाम / Doctor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="doctorName"
                    value={formData.doctorName}
                    onChange={handleInputChange}
                    placeholder="डॉक्टर का नाम / Doctor Name"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.doctorName ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.doctorName && (
                    <p className="text-red-500 text-sm mt-1">{errors.doctorName}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    यु.एच.आई.डी. नंबर / UHID Number
                  </label>
                  <input
                    type="text"
                    name="uhidNumber"
                    value={formData.uhidNumber}
                    onChange={handleInputChange}
                    placeholder="यु.एच.आई.डी. नंबर / UHID Number"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.uhidNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                </div>

                {/* Admission Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    भर्ती दिनांक / Admission Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="admissionDate"
                    value={formData.admissionDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.admissionDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.admissionDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.admissionDate}</p>
                  )}
                </div>

                {/* Discharge/Death Date */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    देहांत/डिस्चार्ज दिनांक / Death/Discharge Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dischargeDeathDate"
                    value={formData.dischargeDeathDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dischargeDeathDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.dischargeDeathDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.dischargeDeathDate}</p>
                  )}
                </div>



                {/* Outcome Type */}
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-semibold mb-2">
                    परिणाम / Outcome <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="outcomeType"
                        value="death"
                        checked={formData.outcomeType === 'death'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">देहांत / Death</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="outcomeType"
                        value="discharge"
                        checked={formData.outcomeType === 'discharge'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">डिस्चार्ज / Discharge</span>
                    </label>
                  </div>
                  {errors.outcomeType && (
                    <p className="text-red-500 text-sm mt-1">{errors.outcomeType}</p>
                  )}
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                संलग्नक / Attachments
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                कृपया निम्नलिखित दस्तावेज़ संलग्न करें / Please attach the following documents
              </p>

              {/* <FileUploadSection
                fieldName="deathCertificateFiles"
                label="मृत्यु प्रमाण पत्र / Death Certificate"
                labelEn="Death Certificate"
                color="red"
              /> */}
              {formData.outcomeType === 'death' && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">
                    मृत्यु प्रमाण पत्र / Death Certificate Upload
                    <span className="text-red-500 ml-1">*</span>
                  </label>

                  <p className="text-sm text-gray-500 mb-2">
                    Death Certificate | You can upload multiple files / आप कई फ़ाइलें अपलोड कर सकते हैं
                  </p>

                  {/* Info message */}
                  {/* <div className="mb-3 p-3 bg-red-50 border-l-4 border-red-500 rounded">
                    <p className="text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <strong>Required:</strong> Death certificate must be uploaded when outcome type is Death
                    </p>
                  </div> */}

                  {/* Show uploaded files */}
                  {deathCertificateFiles.filter(f => f.file).length > 0 && (
                    <div className="space-y-2 mb-3">
                      {deathCertificateFiles.map((fileObj, index) =>
                        fileObj.file && (
                          <div key={fileObj.id} className="flex items-center justify-between bg-red-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">
                                {index + 1}
                              </span>
                              <span className="text-sm text-gray-700 truncate">
                                {fileObj.file.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({(fileObj.file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDeathCertificateFile(index)}
                              className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {/* Drag and Drop Upload Area */}
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${errors.deathCertificateFiles
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300 hover:border-red-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        {deathCertificateFiles.filter(f => f.file).length === 0 ? (
                          <>
                            <Upload className="w-12 h-12 text-gray-400 mb-2" />
                            <span className="text-sm text-gray-600">
                              फ़ाइल अपलोड करें / Click to upload
                            </span>
                          </>
                        ) : (
                          <>
                            <Plus className="w-10 h-10 text-red-600 mb-2" />
                            <span className="text-sm text-red-600 font-semibold">
                              और फ़ाइल जोड़ें / Add More Files
                            </span>
                          </>
                        )}
                        <span className="text-xs text-gray-400 mt-2">
                          JPG, PNG, PDF (Max 5MB each)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Find first empty slot or add new
                            const emptyIndex = deathCertificateFiles.findIndex(f => !f.file);
                            if (emptyIndex !== -1) {
                              handleDeathCertificateFileChange(emptyIndex, file);
                            } else {
                              // Add new slot
                              setDeathCertificateFiles([...deathCertificateFiles, { id: Date.now(), file: file }]);
                            }
                            // Clear input for next selection
                            e.target.value = '';
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {errors.deathCertificateFiles && (
                    <p className="text-red-500 text-sm mt-2">⚠️ {errors.deathCertificateFiles}</p>
                  )}
                </div>
              )}

              <FileUploadSection
                fieldName="claimFormBFiles"
                label="दावा प्रपत्र - B / Claim Form - B"
                labelEn="Claim Form B"
                color="blue"
              />

              <FileUploadSection
                fieldName="claimFormB1Files"
                label="दावा प्रपत्र - B1 / Claim Form - B1"
                labelEn="Claim Form B1"
                color="green"
              />

              <FileUploadSection
                fieldName="policyBondFiles"
                label="पॉलिसी बॉन्ड की प्रति / Policy Bond Copy"
                labelEn="Policy Bond Copy"
                color="purple"
              />

              <FileUploadSection
                fieldName="nomineeIdFiles"
                label="नॉमिनी का पहचान पत्र / Nominee ID Proof"
                labelEn="Nominee ID Proof"
                color="orange"
              />
            </div>

            {/* Applicant Signature Section - NEW */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
                प्रार्थी/प्रार्थिनी के हस्ताक्षर / Applicant Signature <span className="text-red-500">*</span>
              </h2>

              {/* Mode Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('draw');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'draw'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Pen className="w-5 h-5" />
                  Draw Signature / हस्ताक्षर करें
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSignatureMode('upload');
                    clearSignature();
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${signatureMode === 'upload'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image / फ़ोटो अपलोड करें
                </button>
              </div>

              {/* Draw Mode */}
              {signatureMode === 'draw' && (
                <div>
                  <div className={`border-2 rounded-lg overflow-hidden ${errors.applicantSignature ? 'border-red-500' : 'border-gray-300'
                    }`}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-48 bg-white cursor-crosshair',
                        style: { touchAction: 'none' }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex-1 py-2 px-4 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Clear / साफ़ करें
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className="flex-1 py-2 px-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Save / सहेजें
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    👆 Use mouse or finger to draw your signature / अपना हस्ताक्षर बनाने के लिए माउस या उंगली का उपयोग करें
                  </p>
                </div>
              )}

              {/* Upload Mode */}
              {signatureMode === 'upload' && (
                <div>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${errors.applicantSignature ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Upload signature image / हस्ताक्षर की फ़ोटो अपलोड करें
                        </span>
                        <span className="text-xs text-gray-400 mt-2">
                          JPG, PNG (Max 2MB)
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleSignatureUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Signature Preview */}
              {signatureDataURL && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 font-semibold mb-2">
                    Signature Preview / हस्ताक्षर पूर्वावलोकन:
                  </p>
                  <div className="bg-white p-4 rounded border border-gray-300 flex justify-center">
                    <img
                      src={signatureDataURL}
                      alt="Applicant Signature"
                      className="max-h-32 object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Remove / हटाएं
                  </button>
                </div>
              )}

              {errors.applicantSignature && (
                <p className="text-red-500 text-sm mt-2">⚠️ {errors.applicantSignature}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-4 px-6 rounded-lg font-semibold text-white transition-all ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    जमा हो रहा है... / Submitting...
                  </span>
                ) : (
                  'जमा करें / Submit'
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 sm:flex-none px-6 py-4 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                रीसेट करें / Reset
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 text-center font-semibold">
                ⚠️ नोट / Note: आवेदन नॉमिनी की तरफ से दाखिल करना अनिवार्य है!<br />
                Application must be filed by the nominee!
              </p>
            </div>

            {/* Closing Section */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700 text-center">
                अतः श्रीमान्जी से निवेदन है कि प्रार्थी/प्रार्थिनी को संलग्न जीवन बीमा दावा प्रपत्र पूर्ण कराकर देने कि कृपा करें।<br />
                <em className="text-xs">Therefore, it is requested that the applicant be provided with the completed life insurance claim form.</em>
              </p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default LifeInsuranceClaimForm;